/*
 *   File : approve-upload.js
 *   Author : https://evoqins.com
 *   Description : Approve invoice upload modal component
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { memo, useEffect, useState } from 'react';
import { Modal } from 'bootstrap';
import { format, parse, isValid } from 'date-fns';

// Custom component
import { PrimaryButton, SecondaryButton } from '../Buttons';
import { Icon } from '../Icon';
import { CustomDatePicker, CustomSelectBox, CustomTextInput, TextArea } from '../FormElements';
import { APIService } from '../../Service';

// Left preview column width (kept in sync between the image row and the comment indent)
const PREVIEW_WIDTH = 320;

const RECEIPT_DATE_FORMATS = ['dd-MMM-yyyy', 'yyyy-MM-dd', 'dd-MM-yyyy', 'dd/MM/yyyy', 'MMM dd, yyyy'];

const parseReceiptDate = (value) => {
    if (!value) return null;
    if (value instanceof Date && isValid(value)) {
        return value;
    }

    for (const pattern of RECEIPT_DATE_FORMATS) {
        const parsed = parse(value, pattern, new Date());
        if (isValid(parsed)) {
            return parsed;
        }
    }

    return null;
};

const ApproveUploadModal = (props) => {

    const [selectedProduct, setSelectedProduct] = useState(null);

    const [listLoader, setListLoader] = useState(true);
    const [products, setProducts] = useState([]);

    // Receipt information
    const [receiptNumber, setReceiptNumber] = useState("");
    const [receiptDate, setReceiptDate] = useState(null);
    const [dateToggle, setDateToggle] = useState(false);
    const [totalAmount, setTotalAmount] = useState("");

    // Product item
    const [productCode, setProductCode] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [quantity, setQuantity] = useState("");
    const [lineAmount, setLineAmount] = useState("");

    // Comment
    const [comment, setComment] = useState("");

    const currencyCode = props.currencyCode || props.data?.currency_code || "AED";

    useEffect(() => {
        _getProducts();
    }, []);

    // prefill the fields from the selected invoice row (ocr/detail data)
    useEffect(() => {
        const data = props.data || {};
        setReceiptNumber(data.receipt_number ?? "");
        setReceiptDate(parseReceiptDate(data.receipt_date));
        setTotalAmount(data.total_amount ?? "");
        setProductCode(data.product_code || data.code || "");
        setProductDescription(data.product_description ?? data.name ?? "");
        setQuantity(data.quantity ?? "");
        setLineAmount(data.line_amount ?? "");
    }, [props.data]);

    useEffect(() => {
        if (dateToggle === true) {
            const date_modal = new Modal(document.getElementById('approve-receipt-date-modal'));
            date_modal.show();
        }
    }, [dateToggle]);

    useEffect(() => {
        if (props.status === true) {
            _handleCloseModal();
        }
    }, [props.status]);

    // handle close modal
    const _handleCloseModal = () => {
        var button = document.getElementById("approve-upload-close");
        button.click();
        props.handleClose();
        setSelectedProduct(null);
        setComment("");
        setDateToggle(false);
    };

    const _handleReceiptDateToggle = () => {
        setDateToggle(!dateToggle);
    };

    const _handleSelectedReceiptDate = (day) => {
        setReceiptDate(day);
        setDateToggle(false);
        _handleCloseReceiptDateModal();
    };

    const _handleCloseReceiptDateModal = () => {
        const button = document.getElementById("approve-receipt-date-close");
        button?.click();
        setDateToggle(false);
    };

    // normalise the receipt date to the API format (yyyy-MM-dd)
    const _formatReceiptDate = (value) => {
        if (!value) return value;
        if (value instanceof Date && isValid(value)) {
            return format(value, 'yyyy-MM-dd');
        }

        const supported_formats = RECEIPT_DATE_FORMATS;
        for (const pattern of supported_formats) {
            const parsed = parse(value, pattern, new Date());
            if (isValid(parsed)) {
                return format(parsed, 'yyyy-MM-dd');
            }
        }
        return value;
    };

    // autofill product fields from the mapped product (revert to OCR values when cleared)
    const _handleProductSelect = (selected) => {
        setSelectedProduct(selected);
        if (selected) {
            setProductCode(selected.product_code ?? "");
            setProductDescription(selected.description ?? "");
        } else {
            const data = props.data || {};
            setProductCode(data.product_code || data.code || "");
            setProductDescription(data.product_description ?? data.name ?? "");
        }
    };

    const _handleApprove = () => {

        if (selectedProduct !== null) {

            let data = {
                id: props.id,
                product_id: selectedProduct?.value,
                quantity: quantity,
                line_amount: lineAmount,
                product_description: productDescription,
                receipt_number: receiptNumber,
                receipt_date: _formatReceiptDate(receiptDate),
                total_amount: totalAmount,
                corrected_comment: comment,
            }

            props.handleSuccess(data);
        }
    };

    // API - Get OCR products
    const _getProducts = () => {
        let is_post = false;
        let url = 'general/ocr_products';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setProducts(response.data);
            } else {
                setProducts([])
            }
            setListLoader(false);
        });
    };

    // scanned bill preview (image / pdf / fallback)
    const _renderPreview = () => {
        const url = props.fileUrl?.split('?')[0];
        const fileExtension = url?.split('.').pop()?.toLowerCase();
        const isImage = ['jpg', 'jpeg', 'png', 'webp'].includes(fileExtension);
        const isPDF = fileExtension === 'pdf';

        if (!props.fileUrl) {
            return <span className='e-text-raisin-black e-alt-font-noto-sans e-font-14'>No bill available</span>;
        }
        if (isPDF) {
            return (
                <div className='d-flex flex-column gap-3 p-3'>
                    <p className='mb-0 e-alt-font-noto-sans e-font-14 line-height-20px e-text-rich-black e-font-weight-500'>
                        {props.fileUrl?.split('/').pop() || props.fileUrl}
                    </p>
                    <SecondaryButton
                        name={<><Icon icon="download-icon" className="me-1" size={20} /> Download</>}
                        className="e-text-indigo e-border-1px-indigo align-self-start"
                        padding="py-2 px-3"
                        onPress={() => {
                            const link = document.createElement('a');
                            link.href = props.fileUrl;
                            link.download = "invoice";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }}
                    />
                </div>
            );
        }
        return (
            <img src={props.fileUrl}
                className='w-100 h-100'
                style={{ objectFit: 'contain' }}
                alt='invoice' />
        );
    };

    return (
        <div className="modal fade" id="approve-upload-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="approveRequestModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "90vw" }}>
                <div className="modal-content e-border-radius-8 p-4" style={{ maxHeight: "90vh" }}>
                    <div className="modal-header px-0 pt-0 pb-3 border-0 align-items-start">
                        <div>
                            <h4 className="mb-2 e-primary-font-playfair e-text-dark-jungle-green e-font-18 e-font-weight-500 line-height-28px">
                                {props.title}
                            </h4>
                            <p className='mb-0 e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-400 line-height-20px'>
                                Review missing items and confirm to complete the upload.
                            </p>
                        </div>
                        <Icon icon="close-icon"
                            className='cursor-pointer'
                            size="24px"
                            onPress={_handleCloseModal} />
                        <button type="button" id='approve-upload-close' className="btn-close shadow-none d-none" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div className="modal-body w-100 px-0 pb-0 pt-2" style={{ overflowY: "auto", minHeight: 0 }}>

                        {/* Row : scanned bill + receipt / product card */}
                        <div className='d-flex gap-3'>
                            <div style={{ width: PREVIEW_WIDTH, flexShrink: 0 }} className='d-flex'>
                                <div className='e-border-1px-chinese-silver e-border-radius-8 w-100 d-flex align-items-center justify-content-center overflow-hidden'>
                                    {_renderPreview()}
                                </div>
                            </div>

                            <div className='flex-grow-1 e-bg-beautiful-white e-border-radius-16 p-4'>

                                {/* Receipt information */}
                                <p className='mb-0 e-text-indigo e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-24px'>
                                    Receipt information
                                </p>
                                <p className='mb-0 pt-1 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                                    Verify and update the receipt details
                                </p>

                                <div className='d-flex flex-wrap gap-3 pt-3'>
                                    <div style={{ width: 240 }}>
                                        <CustomTextInput label="Receipt number"
                                            placeholder="Receipt number"
                                            type="text"
                                            value={receiptNumber}
                                            className="w-100 e-bg-white"
                                            labelStyle="e-text-davys-grey"
                                            handleChange={(value) => setReceiptNumber(value)} />
                                    </div>
                                    <div style={{ width: 240 }}>
                                        <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-18px padding-6px-bottom'>
                                            Receipt date
                                        </p>
                                        <div className='position-relative'>
                                            <div className={`${dateToggle ? 'e-border-1px-indigo' : 'e-border-1px-columbia-blue'} e-bg-white e-border-radius-8 d-flex align-items-center justify-content-between padding-12px-tb px-3 cursor-pointer`}
                                                onClick={_handleReceiptDateToggle}>
                                                {receiptDate ?
                                                    <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-22px'>
                                                        {format(receiptDate, 'dd-MMM-yyyy')}
                                                    </p>
                                                    :
                                                    <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-22px'>
                                                        DD / MM / YYYY
                                                    </p>
                                                }
                                                {receiptDate ?
                                                    <div onClick={(event) => event.stopPropagation()}>
                                                        <Icon icon="close-icon"
                                                            size={18}
                                                            className='cursor-pointer'
                                                            onPress={() => setReceiptDate(null)} />
                                                    </div>
                                                    :
                                                    <img src={require("../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                        draggable={false}
                                                        alt='calendar-icon' />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ width: 240 }}>
                                        <label className='e-text-davys-grey d-flex e-text-rich-black e-alt-font-noto-sans e-font-weight-400 line-height-18px e-font-14 padding-6px-bottom'>
                                            Total amount
                                        </label>
                                        <div className='e-border-1px-columbia-blue e-border-radius-8 e-bg-white d-flex align-items-center px-3 padding-12px-tb'>
                                            <span className='e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-22px pe-2'>
                                                {currencyCode}
                                            </span>
                                            <input value={totalAmount}
                                                type="text"
                                                placeholder="Total amount"
                                                className='border-0 e-bg-white e-font-14 e-text-raisin-black e-font-weight-400 line-height-22px e-alt-font-noto-sans w-100'
                                                onWheel={() => document.activeElement.blur()}
                                                onChange={(e) => setTotalAmount(e.target.value)} />
                                        </div>
                                    </div>
                                </div>

                                {/* Product items */}
                                <p className='mb-0 pt-3 e-text-indigo e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-24px'>
                                    Product items
                                </p>
                                <p className='mb-0 pt-1 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                                    Review line items from the invoice and map to existing products
                                </p>

                                <div className='e-bg-white e-border-radius-12 p-3 mt-3'>
                                    <div className='d-flex gap-3'>
                                        <div style={{ flex: '0.9 1 0', minWidth: 0 }}>
                                            <CustomTextInput label="Product code"
                                                placeholder="-"
                                                type="text"
                                                disabled={true}
                                                value={productCode}
                                                className="w-100 text-truncate"
                                                labelStyle="e-text-davys-grey text-nowrap"
                                                handleChange={() => { }} />
                                        </div>
                                        <div style={{ flex: '2.2 1 0', minWidth: 0 }}>
                                            <CustomTextInput label="Product description"
                                                placeholder="Product description"
                                                type="text"
                                                value={productDescription}
                                                className="w-100"
                                                labelStyle="e-text-davys-grey text-nowrap"
                                                handleChange={(value) => setProductDescription(value)} />
                                        </div>
                                        <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                            <CustomTextInput label="Quantity"
                                                placeholder="Quantity"
                                                type="text"
                                                value={quantity}
                                                className="w-100"
                                                labelStyle="e-text-davys-grey text-nowrap"
                                                handleChange={(value) => setQuantity(value)} />
                                        </div>
                                        <div style={{ flex: '1.3 1 0', minWidth: 0 }}>
                                            <CustomTextInput label={`Line amount (${currencyCode})`}
                                                placeholder="Line amount"
                                                type="text"
                                                value={lineAmount}
                                                className="w-100"
                                                labelStyle="e-text-davys-grey text-nowrap"
                                                handleChange={(value) => setLineAmount(value)} />
                                        </div>
                                        <div style={{ flex: '2.5 1 0', minWidth: 0 }} className={`${listLoader ? "pe-none opacity-75" : ""} position-relative`}>
                                            <CustomSelectBox label="Map to Existing product"
                                                placeholder="Search and select"
                                                value={selectedProduct}
                                                isMulti={false}
                                                options={products}
                                                padding="8px 14px 8px 40px"
                                                className="e-modal-select e-bg-white e-border-radius-12"
                                                labelClass="e-text-davys-grey text-nowrap"
                                                isSearchable={true}
                                                selectChange={_handleProductSelect}
                                            />
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6E7079" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className='position-absolute' style={{ left: 14, top: 39, pointerEvents: 'none' }}>
                                                <circle cx="11" cy="11" r="7" />
                                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Row : comment (indented to align under the card) */}
                        <div className='d-flex gap-3 pt-3'>
                            <div style={{ width: PREVIEW_WIDTH, flexShrink: 0 }} />
                            <div className='flex-grow-1 e-border-1px-platinum-light e-border-radius-16 p-4'>
                                <p className='mb-0 e-text-indigo e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-24px'>
                                    Comment
                                </p>
                                <p className='mb-0 pt-1 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                                    Add any remarks or comment about your decision for sending to OCR for future learning
                                </p>
                                <div className='pt-3'>
                                    <TextArea placeholder="Add remarks or comment"
                                        value={comment}
                                        rows={2}
                                        className="w-100 px-3 py-2 e-font-14 e-text-raisin-black e-alt-font-noto-sans"
                                        handleChange={(value) => setComment(value)} />
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Footer actions */}
                    <div className='d-flex gap-3 align-items-center justify-content-end pt-3'>
                        <div style={{ width: 170 }}>
                            <SecondaryButton name="Cancel"
                                className="w-100"
                                onPress={_handleCloseModal} />
                        </div>
                        <div style={{ width: 170 }}>
                            <PrimaryButton name={props.primaryButtonLabel}
                                className="w-100"
                                status={props.loader}
                                disabled={selectedProduct === null}
                                onPress={_handleApprove} />
                        </div>
                    </div>
                </div>
            </div>

            {dateToggle &&
                <div className="modal fade" id="approve-receipt-date-modal" data-bs-backdrop="false" tabIndex="-1" aria-labelledby="approveReceiptDateModalLabel" aria-hidden="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            _handleCloseReceiptDateModal();
                        }
                    }}>
                    <div className="modal-dialog modal-dialog-centered e-border-radius-24" onClick={(event) => event.stopPropagation()}>
                        <button id='approve-receipt-date-close'
                            className='d-none'
                            data-bs-dismiss="modal" />
                        <div className="modal-content w-358px mx-auto border-0 bg-transparent e-date-modal-box-shadow">
                            <CustomDatePicker type="modal"
                                disableFuture={true}
                                selected={receiptDate}
                                onSelect={_handleSelectedReceiptDate} />
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}

export default memo(ApproveUploadModal);
